import React, {ChangeEvent, FormEvent, RefObject} from 'react';
import {formatDuration, formatFileSize} from '../utils';
import HomeModalShell from './HomeModalShell';
import styles from '../Home.module.css';

interface UploadReelModalProps {
  isOpen: boolean;
  loading: boolean;
  metadataLoading: boolean;
  error: string | null;
  validationError: string | null;
  statusMessage: string | null;
  uploadProgressPercent: number | null;
  title: string;
  description: string;
  isFeatured: boolean;
  selectedVideoFile: File | null;
  selectedVideoPreviewUrl: string;
  selectedVideoDurationSeconds?: number;
  titleInputRef: RefObject<HTMLInputElement>;
  fileInputRef: RefObject<HTMLInputElement>;
  onTitleChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onFeaturedChange: (isFeatured: boolean) => void;
  onClose: () => void;
  onBrowseVideo: () => void;
  onRemoveVideo: () => void;
  onVideoChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

const UploadReelModal: React.FC<UploadReelModalProps> = ({
  isOpen,
  loading,
  metadataLoading,
  error,
  validationError,
  statusMessage,
  uploadProgressPercent,
  title,
  description,
  isFeatured,
  selectedVideoFile,
  selectedVideoPreviewUrl,
  selectedVideoDurationSeconds,
  titleInputRef,
  fileInputRef,
  onTitleChange,
  onDescriptionChange,
  onFeaturedChange,
  onClose,
  onBrowseVideo,
  onRemoveVideo,
  onVideoChange,
  onSubmit,
}) => {
  if (!isOpen) {
    return null;
  }

  return (
    <HomeModalShell
      backdropClassName={styles.modalBackdrop}
      panelClassName={styles.modalCard}
      ariaLabelledBy="upload-reel-modal-title"
      ariaDescribedBy="upload-reel-modal-description"
      onClose={onClose}
    >
      <div className={styles.modalHeader}>
        <div>
          <h2 id="upload-reel-modal-title" className={styles.modalTitle}>Upload a New Reel</h2>
          <p id="upload-reel-modal-description" className={styles.modalDescription}>
            Add a title, optional description, and a video file to publish a new highlight reel to Live Reels.
          </p>
        </div>
        <button
          type="button"
          className={styles.modalClose}
          onClick={onClose}
          aria-label="Close upload reel modal"
        >
          ×
        </button>
      </div>

      <form onSubmit={onSubmit} className={styles.modalForm}>
        <label htmlFor="upload-reel-title" className={styles.modalLabel}>
          Reel Title
        </label>
        <input
          id="upload-reel-title"
          ref={titleInputRef}
          value={title}
          onChange={(event) => onTitleChange(event.target.value)}
          className={styles.modalInput}
          placeholder="Give your reel a short, punchy title"
          maxLength={100}
          disabled={loading}
        />

        <label htmlFor="upload-reel-description" className={styles.modalLabel}>
          Description
          <span className={styles.modalLabelHint}>Optional</span>
        </label>
        <textarea
          id="upload-reel-description"
          value={description}
          onChange={(event) => onDescriptionChange(event.target.value)}
          className={`${styles.modalTextarea} ${styles.modalTextareaShort}`}
          placeholder="Tell viewers what this reel is about"
          maxLength={500}
          disabled={loading}
        />

        <label htmlFor="upload-reel-video-file" className={styles.modalLabel}>
          Video File
        </label>
        <input
          id="upload-reel-video-file"
          ref={fileInputRef}
          type="file"
          accept="video/mp4,video/quicktime,video/x-msvideo,.mp4,.mov,.avi"
          className={styles.modalFileInput}
          onChange={onVideoChange}
          disabled={loading}
        />
        <div className={styles.modalUploadRow}>
          <button
            type="button"
            className={styles.modalUploadButton}
            onClick={onBrowseVideo}
            disabled={loading}
          >
            Browse Video
          </button>
          <div className={styles.modalUploadMeta}>
            <span className={styles.modalUploadName}>
              {selectedVideoFile ? selectedVideoFile.name : 'No video selected'}
            </span>
            <span className={styles.modalUploadHint}>MP4, MOV, AVI up to 500 MB</span>
          </div>
          {selectedVideoFile && (
            <button
              type="button"
              className={styles.modalRemoveButton}
              onClick={onRemoveVideo}
              disabled={loading}
            >
              Remove
            </button>
          )}
        </div>

        <label className={styles.featuredToggle}>
          <input
            type="checkbox"
            checked={isFeatured}
            onChange={(event) => onFeaturedChange(event.target.checked)}
            disabled={loading}
            className={styles.featuredCheckbox}
            style={{'--featured-checkbox-icon-url': `url("${process.env.PUBLIC_URL}/icons/check-rounded.svg")`} as React.CSSProperties}
          />
          <span>Feature this reel in Live Reels</span>
        </label>
        <p className={styles.featuredHint}>
          Featured reels are pinned to the top of the reel feed until another featured reel replaces them.
        </p>

        {selectedVideoPreviewUrl && (
          <div className={styles.modalPreview}>
            <div className={styles.modalPreviewHeader}>
              <span>Preview</span>
              <span className={styles.reelPreviewMeta}>
                {selectedVideoFile ? formatFileSize(selectedVideoFile.size) : ''}
                {' · '}
                {metadataLoading ? 'Reading duration...' : formatDuration(selectedVideoDurationSeconds)}
              </span>
            </div>
            <video
              src={selectedVideoPreviewUrl}
              className={styles.reelPreviewVideo}
              controls
              preload="metadata"
            />
          </div>
        )}

        {validationError && (
          <div className={`${styles.modalMessage} ${styles.modalValidationMessage}`}>
            {validationError}
          </div>
        )}

        {statusMessage && (
          <div className={`${styles.modalMessage} ${styles.modalInfoMessage}`}>
            <div className={styles.modalStatusHeader}>
              <span>{statusMessage}</span>
              {typeof uploadProgressPercent === 'number' ? <span>{uploadProgressPercent}%</span> : null}
            </div>
            {typeof uploadProgressPercent === 'number' ? (
              <div className={styles.modalProgressTrack} aria-hidden="true">
                <div
                  className={styles.modalProgressFill}
                  style={{width: `${Math.min(Math.max(uploadProgressPercent, 0), 100)}%`}}
                />
              </div>
            ) : null}
          </div>
        )}

        {error && (
          <div className={`${styles.modalMessage} ${styles.modalErrorMessage}`}>
            {error}
          </div>
        )}

        <div className={styles.modalActions}>
          <button
            type="button"
            className={`${styles.modalButton} ${styles.modalButtonSecondary}`}
            onClick={onClose}
          >
            {loading ? 'Cancel Upload' : 'Cancel'}
          </button>
          <button
            type="submit"
            className={`${styles.modalButton} ${styles.modalButtonPrimary}`}
            disabled={loading || metadataLoading}
          >
            {loading ? 'Uploading Reel...' : metadataLoading ? 'Preparing Video...' : 'Upload Reel'}
          </button>
        </div>
      </form>
    </HomeModalShell>
  );
};

export default React.memo(UploadReelModal);
